import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import BEMHelper from '@ynap/bem-helper';
import { AccordionSection } from '@ynap/accordion';
import { getEditorials, editorialIds } from './getEditorials';
import { useDeliveryInfo } from '@ynap/delivery-hook';
import ErrorBoundary from './ErrorBoundary';
import WIWImageInformation from '../WIWImageInformation/WIWImageInformation';
import { useRecommendations } from '@ynap/recommendations-hook';
import { useConfig } from '@ynap/immutables-provider';
import ManufacturerDetails from './ManufacturerDetails';

const bem = new BEMHelper('EditorialAccordion');

//this is where the getEditorials is mapped across to
const EditorialAccordion = (props) => {
    const {
        finalSale,
        sizeDetails,
        toggle,
        activeSection,
        className,
        onOpen = () => {},
        modifier,
        fullfilledBy,
        onLinkClick = () => {},
        accordionRef,
        variation,
        setIsSizeFitScroll,
        setDisableOneSizeLeft = () => {},
        selectedSize,
        disableOneSizeLeft,
        sizeLabel,
        isEip,
        enableDeliveryAndReturnCustomMessage,
        country,
        colourPartNumber,
        messages,
        designerIdentifier,
        shouldDisableSizeChartAndMessage,
        trackEvent,
        ...rest
    } = props;
    const [doFetchDelivery, setDoFetchDelivery] = useState(false);
    const { deliveryInfo, dropshipInfo, shippingToCountry, deliveryLink, returnsLink, isLoading } = useDeliveryInfo(doFetchDelivery, fullfilledBy || null);
    const editorials = getEditorials({
        bem,
        shippingToCountry,
        deliveryInfo,
        dropshipInfo,
        deliveryLink,
        returnsLink,
        fullfilledBy,
        ...rest,
        sizeDetails,
        onLinkClick,
        finalSale,
        isEip,
        enableDeliveryAndReturnCustomMessage,
        country,
        shouldDisableSizeChartAndMessage,
    });
    const [accordions, setAccordions] = useState(editorials);
    const defaultOpenIndex = props.features?.isWatchExperts
        ? accordions.findIndex((editorial) => editorial.id == editorialIds.DETAILS_AND_CARE)
        : props.activeSectionIndex;
    const [activeIndex, setActiveIndex] = useState(defaultOpenIndex || 0);
    const previous = useRef();
    const config = useConfig();

    useEffect(() => {
        if (previous.current?.props?.toggle !== toggle) {
            let activeSectionIndex = accordions.findIndex((editorial) => editorial.id == activeSection);
            activeSectionIndex >= 0 && setActiveIndex(activeSectionIndex); //If no header matches (-1) then return null
        }
        if (previous.current?.deliveryInfoLoading && !isLoading) {
            let deliveryIndex = accordions.findIndex((editorial) => editorial.id == editorialIds.DELIVERY_AND_RETURNS);
            setActiveIndex(deliveryIndex);
        }
        if (activeSection === 'CONSIDERED_INFO') {
            setActiveIndex(2);
            accordionRef.current.scrollIntoView();
            onOpen('Considered');
        }
        previous.current = { props, deliveryInfoLoading: isLoading };
    }, [deliveryInfo, toggle, activeSection, accordions]);

    useEffect(() => {
        const sizeAndFitIndex = accordions.findIndex((editorial) => editorial.id == editorialIds.SIZE_AND_FIT);
        if (sizeAndFitIndex === -1) {
            return setDisableOneSizeLeft(false);
        }
        if (['v2_web00125_movetop', 'v2_web00124_movetop', 'v2_web00091_movetop', 'v2_web00093_movetop'].includes(variation)) {
            setDisableOneSizeLeft(false);
            if (sizeAndFitIndex != 0) {
                const newAccordions = [...accordions];
                newAccordions.unshift(newAccordions.splice(sizeAndFitIndex, 1)[0]);
                setAccordions(newAccordions);
                setActiveIndex(0);
            }
        }
        if (['v3_web00124_sizeselected', 'v3_web00125_sizeselected', 'v3_web00091_sizeselected', 'v3_web00093_sizeselected'].includes(variation)) {
            setDisableOneSizeLeft(false);
            setIsSizeFitScroll(true);
            if (activeSection === editorialIds.SIZE_AND_FIT && (selectedSize || sizeLabel)) {
                variation === 'v3_web00124_sizeselected' && setIsSizeFitScroll(false);
                setActiveIndex(sizeAndFitIndex);
            }
        }
        if ((!variation || variation === 'off' || variation === 'control' || variation === 'aa_control' || variation === 'aa_variation') && !defaultOpenIndex) {
            setDisableOneSizeLeft(false);
            setActiveIndex(0);
        }
    }, [variation, accordions, activeSection, disableOneSizeLeft && (selectedSize || sizeLabel)]);

    const mapVariationHandler = !variation || ['off', 'control', 'aa_control', 'aa_variation'].includes(variation) ? editorials : accordions;

    const renderEditorsNotesWiwInformation = () => {
        const recommendations = useRecommendations({
            ...props,
            partNumber: colourPartNumber,
            recommendationsToFetch: ['WIW_ProductRec'],
            originalOutfit: true,
        });

        if (!recommendations.WIW_ProductRec) return null;

        return <WIWImageInformation WIWProductRec={recommendations.WIW_ProductRec} messages={messages} trackEvent={trackEvent} />;
    };
    const { minimumPriceCountries = [] } = config?.head || {};
    const isEUcountry = minimumPriceCountries?.includes(country?.toUpperCase?.());
    const showOriginalOutfits = config?.head?.pdp?.showOriginalOutfits;
    return (
        <div ref={accordionRef} className={bem(null, modifier, className)}>
            {mapVariationHandler.map((editorial, index) => {
                const isEditorsNotes = editorial.id === editorialIds?.EDITORS_NOTES;
                const isDetailAndCare = editorial.id === editorialIds?.DETAILS_AND_CARE || editorial.id === editorialIds?.TECHNICAL_DESCRIPTION;
                return (
                    editorial.content && (
                        <ErrorBoundary key={editorial.id} selectedPartNumber={props.selectedPartNumber} editorialId={editorial.id}>
                            <AccordionSection
                                heading={<div className={bem('accordionTitle', modifier)}>{editorial.heading}</div>}
                                content={
                                    <div className={bem('accordionContentDiv')}>
                                        <div className={bem('accordionContent', editorial?.id?.toLowerCase())}>{editorial.content}</div>
                                        {isEditorsNotes && showOriginalOutfits && renderEditorsNotesWiwInformation()}
                                        {isEUcountry && isDetailAndCare && <ManufacturerDetails messages={messages} designerIdentifier={designerIdentifier} />}
                                    </div>
                                }
                                uniqueId={editorial.heading}
                                isActive={activeIndex === index}
                                onClickOpen={() => {
                                    onOpen(editorial.heading);
                                    if (!doFetchDelivery && !fullfilledBy && editorial.id === editorialIds?.DELIVERY_AND_RETURNS) {
                                        setDoFetchDelivery(true);
                                    }
                                    setActiveIndex(index);
                                }}
                                onClickClose={() => {
                                    setActiveIndex(null);
                                }}
                                sectionId={editorial.id}
                                modifier={modifier}
                                config={config}
                                country={country}
                            />
                        </ErrorBoundary>
                    )
                );
            })}
        </div>
    );
};

EditorialAccordion.propTypes = {
    finalSale: PropTypes.bool,
    activeSectionIndex: PropTypes.num,
    activeSection: PropTypes.string,
    toggle: PropTypes.bool,
    editorials: PropTypes.object,
    className: PropTypes.string,
    onOpen: PropTypes.func,
    onLinkClick: PropTypes.func,
    modifier: PropTypes.string,
    selectedPartNumber: PropTypes.string,
    fullfilledBy: PropTypes.string,
    sizeDetails: PropTypes.func,
    variation: PropTypes.string,
    setIsSizeFitScroll: PropTypes.func,
    setDisableOneSizeLeft: PropTypes.func,
};

export default EditorialAccordion;
